import { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface DickinsonTextParserProps {
  onImportComplete: () => void;
}

interface ImportResult {
  successful: number;
  failed: number;
  total: number;
  failedRecords: { row: number; reason: string; data: any }[];
}

export function DickinsonTextParser({ onImportComplete }: DickinsonTextParserProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [pastedText, setPastedText] = useState("");

  const parseDate = (dateStr: string): string => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    const parts = dateStr.trim().split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return new Date().toISOString().split('T')[0];
  };

  const analyzeOwnerFinance = (grantee: string, grantor: string) => {
    const bankKeywords = ['BANK', 'MORTGAGE', 'LENDING', 'FINANCE COMPANY', 'CREDIT UNION'];
    const familyKeywords = [' JR', ' SR', ' III', ' II', 'ESTATE OF', 'TRUST', 'FAMILY TRUST', 'LIVING TRUST', 'REVOCABLE TRUST'];
    const businessKeywords = ['LLC', 'INC', 'CORP', 'LTD', 'COMPANY', 'CO.', 'CORPORATION', 'LIMITED'];
    
    const granteeUpper = grantee?.toUpperCase() || '';
    const grantorUpper = grantor?.toUpperCase() || '';
    
    const reasons: string[] = [];
    
    // EXCLUDE: Bank involvement
    if (bankKeywords.some(k => granteeUpper.includes(k) || grantorUpper.includes(k))) {
      reasons.push('❌ EXCLUDED: Bank keywords found');
      return { isOwnerFinance: false, score: 0, reasons };
    }
    
    // EXCLUDE: Family relationships (keywords)
    const hasFamilyKeywords = familyKeywords.some(k => 
      granteeUpper.includes(k) || grantorUpper.includes(k)
    );
    
    if (hasFamilyKeywords) {
      reasons.push('❌ EXCLUDED: Family relationship detected (JR/SR/TRUST/ESTATE)');
      return { isOwnerFinance: false, score: 0, reasons };
    }
    
    // EXCLUDE: Same last name (likely family transfer)
    const grantorParts = grantorUpper.split(/\s+/);
    const granteeParts = granteeUpper.split(/\s+/);
    const grantorLastName = grantorParts[grantorParts.length - 1];
    const granteeLastName = granteeParts[granteeParts.length - 1];
    
    if (grantorLastName && granteeLastName && 
        grantorLastName === granteeLastName && 
        grantorLastName.length > 3) {
      reasons.push('❌ EXCLUDED: Same last name (likely family transfer)');
      return { isOwnerFinance: false, score: 0, reasons };
    }
    
    // EXCLUDE: Buyer (grantee) is a business entity
    const buyerIsBusiness = businessKeywords.some(k => granteeUpper.includes(k));
    if (buyerIsBusiness) {
      reasons.push('❌ EXCLUDED: Buyer is a business entity');
      return { isOwnerFinance: false, score: 0, reasons };
    }
    
    // If we got here, it's a valid land contract
    reasons.push('✅ Valid land contract: Individual buyer, not family, no bank');
    return { isOwnerFinance: true, score: 100, reasons };
  };

  const parseDickinsonText = (text: string) => {
    console.log('🔍 Starting Dickinson parse, file length:', text.length);
    
    // First split into lines, THEN normalize each line individually
    const rawLines = text.split('\n');
    console.log('📝 Raw lines found:', rawLines.length);
    
    const lines = rawLines.map(line => 
      line
        .replace(/\uFFFD/g, ' ')        // Replace � with space
        .replace(/[^\x00-\x7F\n]/g, ' ') // Replace non-ASCII (except newlines)
        .replace(/\s+/g, ' ')            // Normalize multiple spaces
        .trim()
    );
    
    console.log('📝 Processing', lines.length, 'normalized lines');
    
    const records: any[] = [];
    let currentRecord: any = null;
    let recordCount = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // Skip lines with just arrows/separators
      if (trimmed.startsWith('>>>>>>')) continue;
      
      // Check if this is a date line - be more flexible with spacing
      const dateMatch = trimmed.match(/(\d{2}\/\d{2}\/\d{4})\s+(\S+)\s+(\S+)\s+(.+)/);
      if (dateMatch) {
        console.log(`📅 Found date line ${i}: ${dateMatch[1]}, doc: ${dateMatch[2]}`);
        
        // Save previous record if it has both grantor and grantee
        if (currentRecord && currentRecord.grantor && currentRecord.grantee) {
          records.push(currentRecord);
          recordCount++;
          console.log(`✅ Saved record #${recordCount}:`, currentRecord.grantor, '→', currentRecord.grantee);
        }
        
        // Start new record
        currentRecord = {
          filing_date: dateMatch[1],
          document_number: dateMatch[2],
          reference_number: dateMatch[3],
          document_type: dateMatch[4].trim(),
          grantor: null,
          grantee: null,
          grantee_address: null,
          property_description: null,
          parcel_number: null,
          municipality: null
        };
        continue;
      }
      
      if (!currentRecord) continue;
      
      // Parse GRANTOR line - handle optional X marker and quotes
      if (trimmed.includes('GRANTOR')) {
        // Remove quotes, X markers, and extra spaces
        const cleanLine = trimmed.replace(/^["']|["']$/g, '').replace(/X\s+/g, '');
        const nameMatch = cleanLine.match(/GRANTOR\s+(.+)/);
        if (nameMatch) {
          const name = nameMatch[1].trim();
          if (name && currentRecord) {
            currentRecord.grantor = name;
            console.log(`👤 Grantor: ${name}`);
          }
        }
      }
      
      // Parse GRANTEE line - handle optional X marker and quotes
      if (trimmed.includes('GRANTEE')) {
        // Remove quotes, X markers, and extra spaces
        const cleanLine = trimmed.replace(/^["']|["']$/g, '').replace(/X\s+/g, '');
        const nameMatch = cleanLine.match(/GRANTEE\s+(.+)/);
        if (nameMatch) {
          const name = nameMatch[1].trim();
          if (name && currentRecord) {
            currentRecord.grantee = name;
            console.log(`👥 Grantee: ${name}`);
          }
        }
      }
      
      // Parse ADDRESS line
      if (trimmed.includes('ADDRESS')) {
        const addrMatch = trimmed.match(/ADDRESS\s+\/(.+)$/);
        if (addrMatch) {
          currentRecord.grantee_address = addrMatch[1].trim();
        }
      }
      
      // Parse property description (SEC lines)
      if (trimmed.includes('SEC ')) {
        const descMatch = trimmed.match(/SEC\s+(.+)$/);
        if (descMatch) {
          const fullDesc = descMatch[1].trim();
          // Municipality is usually at the end
          const parts = fullDesc.split(/\s{2,}/);
          if (parts.length > 1) {
            currentRecord.municipality = parts[parts.length - 1];
            currentRecord.property_description = parts.slice(0, -1).join(' ');
          } else {
            currentRecord.property_description = fullDesc;
          }
        }
      }
      
      // Parse parcel number (ends with pattern XX-XXX-XXX-XXX-XX)
      const parcelMatch = trimmed.match(/(\d{2}-\d{3}-\d{3}-\d{3}-\d{2})$/);
      if (parcelMatch) {
        currentRecord.parcel_number = parcelMatch[1];
      }
    }
    
    // Don't forget the last record
    if (currentRecord && currentRecord.grantor && currentRecord.grantee) {
      records.push(currentRecord);
      recordCount++;
      console.log(`✅ Saved final record #${recordCount}`);
    }
    
    console.log(`🎉 Parse complete: ${records.length} total records extracted`);
    return records;
  };

  const processText = async (text: string) => {
    setIsProcessing(true);

    try {
      const parsedRecords = parseDickinsonText(text);
      
      if (parsedRecords.length === 0) {
        toast({
          title: "No records found",
          description: "Could not parse any records from the file",
          variant: "destructive"
        });
        setIsProcessing(false);
        return;
      }

      setProgress({ current: 0, total: parsedRecords.length });
      const importedRecords: any[] = [];
      const failedRecords: { row: number; reason: string; data: any }[] = [];
      let ownerFinanceCount = 0;

      const duplicates: string[] = [];
      
      for (let i = 0; i < parsedRecords.length; i++) {
        const record = parsedRecords[i];
        
        // Check if this document already exists
        const { data: existingDoc } = await supabase
          .from('county_research')
          .select('id')
          .eq('county', 'dickinson')
          .eq('document_number', record.document_number)
          .maybeSingle();

        if (existingDoc) {
          // Skip duplicate document numbers silently
          setProgress({ current: i + 1, total: parsedRecords.length });
          continue;
        }
        
        // Check if this seller (grantor) already exists
        const { data: existingSeller } = await supabase
          .from('county_research')
          .select('id, document_number, filing_date')
          .eq('county', 'dickinson')
          .ilike('grantor_name', record.grantor)
          .maybeSingle();

        if (existingSeller) {
          duplicates.push(`Duplicate seller "${record.grantor}" (doc: ${record.document_number}, already in system as ${existingSeller.document_number})`);
          setProgress({ current: i + 1, total: parsedRecords.length });
          continue; // Skip this duplicate seller
        }
        
        const analysis = analyzeOwnerFinance(record.grantee, record.grantor);
        
        // Skip if family-to-family or bank-related
        if (!analysis.isOwnerFinance) {
          console.log(`⏭️ Skipping record: ${record.grantor} → ${record.grantee}`, analysis.reasons);
          setProgress({ current: i + 1, total: parsedRecords.length });
          continue;
        }
        
        const dbRecord = {
          county: 'dickinson',
          document_number: record.document_number,
          filing_date: parseDate(record.filing_date),
          document_type: record.document_type || 'LAND CONTRACT MEMORANDUM',
          grantor_name: record.grantor,
          grantee_name: record.grantee,
          grantee_address: record.grantee_address,
          property_address: record.property_description,
          legal_description: record.property_description,
          is_potential_owner_finance: analysis.isOwnerFinance,
          confidence_score: analysis.score,
          detection_reasons: analysis.reasons,
          status: record.grantee_address ? 'ready_for_melissa' : 'new',
          address_needs_review: !record.grantee_address,
          is_active: true,
          pdf_downloaded: false,
          pdf_parsed: false,
          ocr_attempted: false,
          raw_data: {
            municipality: record.municipality,
            parcel_number: record.parcel_number,
            reference_number: record.reference_number
          }
        };

        const { data, error } = await supabase
          .from('county_research')
          .insert(dbRecord)
          .select()
          .single();

        if (error) {
          failedRecords.push({ 
            row: i + 1, 
            reason: error.message,
            data: { docNum: record.document_number, grantor: record.grantor, grantee: record.grantee }
          });
        } else if (data) {
          importedRecords.push(data);
          if (analysis.isOwnerFinance) ownerFinanceCount++;
        }

        setProgress({ current: i + 1, total: parsedRecords.length });
      }

      setImportResult({
        successful: importedRecords.length,
        failed: failedRecords.length,
        total: parsedRecords.length,
        failedRecords
      });

      onImportComplete();

      // Show duplicates warning if any found
      if (duplicates.length > 0) {
        toast({
          title: "⚠️ Duplicate Sellers Detected",
          description: `Found ${duplicates.length} duplicate seller(s). Check console for details.`,
          variant: "destructive",
          duration: 10000
        });
        console.log('🔍 DUPLICATE SELLERS:', duplicates);
      }

      if (importedRecords.length === 0) {
        toast({
          title: "Import failed",
          description: `No records imported. ${failedRecords.length} failed. Some may have been filtered out (family transfers, banks).`,
          variant: "destructive"
        });
      } else if (failedRecords.length > 0) {
        toast({
          title: "Partial import",
          description: `Imported ${importedRecords.length} of ${parsedRecords.length} records. ${duplicates.length > 0 ? `⚠️ ${duplicates.length} duplicate sellers found.` : ''}`
        });
      } else {
        toast({
          title: "Import complete",
          description: `Imported ${importedRecords.length} records. Found ${ownerFinanceCount} potential deals. ${duplicates.length > 0 ? `⚠️ ${duplicates.length} duplicate sellers detected.` : ''}`
        });
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setPastedText('');

    } catch (error) {
      console.error('Error processing text:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "Failed to process data",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    await processText(text);
  };

  const handlePasteImport = async () => {
    if (!pastedText.trim()) {
      toast({
        title: "No data",
        description: "Please paste some text first",
        variant: "destructive"
      });
      return;
    }
    await processText(pastedText);
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Card className={importResult.failed > 0 ? "border-destructive" : "border-green-600"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResult.failed > 0 ? (
                <AlertCircle className="h-5 w-5 text-destructive" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{importResult.successful}</div>
                <div className="text-sm text-muted-foreground">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">{importResult.failed}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{importResult.total}</div>
                <div className="text-sm text-muted-foreground">Total Rows</div>
              </div>
            </div>

            {importResult.failedRecords.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Failed Records:</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {importResult.failedRecords.map((fail, idx) => (
                    <div key={idx} className="bg-destructive/10 p-3 rounded-lg text-sm">
                      <div className="font-medium">Row {fail.row}</div>
                      <div className="text-destructive">{fail.reason}</div>
                      {fail.data && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {JSON.stringify(fail.data, null, 2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setImportResult(null)}
              className="w-full"
            >
              Clear Results
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Import Dickinson County Records</CardTitle>
          <CardDescription>
            Paste raw text copied from Dickinson County search results
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              How to Use
            </h4>
            <ol className="text-sm space-y-1 text-muted-foreground list-decimal list-inside">
              <li>Copy the search results from Dickinson County website</li>
              <li>Paste directly into the text area below</li>
              <li>Click "Import Records" to process</li>
            </ol>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Paste Dickinson County Data</label>
            <textarea
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder="Paste the raw text from Dickinson County here..."
              disabled={isProcessing}
              className="w-full h-64 p-3 border rounded-md font-mono text-sm resize-y"
            />
          </div>

          <Button
            onClick={handlePasteImport}
            disabled={isProcessing || !pastedText.trim()}
            className="w-full"
            size="lg"
          >
            <Upload className="mr-2 h-4 w-4" />
            {isProcessing ? "Processing..." : "Import Records"}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or upload file</span>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.csv"
            onChange={handleFileUpload}
            className="hidden"
            disabled={isProcessing}
          />

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            variant="outline"
            className="w-full"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload File Instead
          </Button>

          {isProcessing && progress.total > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing records...</span>
                    <span>{progress.current} / {progress.total}</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
